import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const benefits = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    title: 'Organização total',
    description: 'Todos os seus clientes e datas de manutenção em um só lugar. Sem planilha, sem caderno, sem post-it. Tudo organizado e acessível.',
    color: 'text-cyan',
    bg: 'bg-cyan/10 border-cyan/20',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    title: 'Previsibilidade de faturamento',
    description: 'Saiba com antecedência quais clientes precisam de manutenção nos próximos meses. Planeje sua agenda e preveja sua receita com clareza.',
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/20',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
    title: 'Aumento de faturamento',
    description: 'Clientes que já compraram de você têm muito mais chance de comprar de novo. Você só precisa lembrar de entrar em contato — e o sistema faz isso por você.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10 border-yellow-500/20',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Você no controle total',
    description: 'O sistema não fala com seus clientes. Você recebe o lembrete e decide como, quando e pelo que canal entrar em contato. Sua relação com o cliente é sempre sua.',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10 border-purple-500/20',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    title: 'Mensagens pelo WhatsApp',
    description:
      'Você já usa o WhatsApp todos os dias. Os lembretes chegam como mensagens para o seu número — na hora certa, o aviso aparece ali. Cadastro e agenda ficam no RetornaAr; o que pipoca no dia a dia é o lembrete, onde você já está.',
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/20',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2"/>
      </svg>
    ),
    title: 'Simples e rápido de usar',
    description: 'Cadastre um serviço em menos de 1 minuto. Sem treinamento, sem manual, sem complicação. Se você sabe usar o WhatsApp, você sabe usar o RetornaAr.',
    color: 'text-cyan',
    bg: 'bg-cyan/10 border-cyan/20',
  },
];

const BenefitsSection: React.FC = () => {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.delay || 0);
            setTimeout(() => {
              el.classList.remove('reveal-hidden');
              el.classList.add('reveal-visible');
            }, delay);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="beneficios" className="section-padding bg-off-white">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div ref={headerRef} className="reveal-hidden text-center max-w-2xl mx-auto mb-16">
          <span className="tag-badge bg-navy/10 text-navy border border-navy/20 mb-5 inline-block">
            Por que usar o RetornaAr
          </span>
          <h2 className="font-fraunces text-navy text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-5">
            Organização, previsibilidade
            <span className="italic text-cyan-dark"> e mais faturamento.</span>
          </h2>
          <p className="text-text-muted text-lg font-light leading-relaxed">
            Tudo isso sem mudar a forma como você trabalha — só adicionando um sistema que te lembra do que importa.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              data-delay={i * 100}
              className="reveal-hidden bg-white rounded-2xl p-7 border border-border-light hover:shadow-md transition-shadow duration-300"
            >
              <div className={`w-14 h-14 rounded-2xl border ${benefit.bg} flex items-center justify-center ${benefit.color} mb-5`}>
                {benefit.icon}
              </div>
              <h3 className="font-fraunces text-navy text-xl font-medium mb-3">
                {benefit.title}
              </h3>
              <p className="text-text-muted text-sm leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        {/* <div
          className="mt-12 p-8 rounded-2xl bg-navy text-white flex flex-col md:flex-row items-center justify-between gap-6 reveal-hidden"
          ref={(el) => { cardsRef.current[6] = el; }}
          data-delay="700"
        >
          <div>
            <p className="font-fraunces text-2xl md:text-3xl font-light mb-1">
              Pronto para parar de perder receita?
            </p>
            <p className="text-white/60 font-light">
              Teste sem compromisso. Cancele em até 7 dias se quiser.
            </p>
          </div>
          <Link
            to="/login"
            className="btn-primary flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold sm:px-7 sm:py-3.5"
          >
            Quero testar grátis
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div> */}
      </div>
    </section>
  );
};

export default BenefitsSection;
